/**
 * The quotation's lines, optionally grouped by where the price came from
 * (prototype `vQuote`'s `grp` / `STATE.quoteGrouped`).
 *
 * The grouping is not cosmetic: provision lines are priced from a standing contract and RFQ
 * lines from a competitive quote, and a purchaser reviews the two differently. Each group
 * carries its own cost/sell subtotal.
 */

import { Fragment, useState } from 'react'
import { Money } from '../../components/Money'
import { Button, Card } from '../../components/ui'
import { convert } from '../../lib/fx'
import { effectiveLines } from '../../lib/pricing'
import { QUOTE_PAGE_SIZE, matchingLines, quoteLineView } from '../../lib/quoteLineView'
import { QuoteRow } from './QuoteRow'
import type { WireFx } from '../../lib/api/wire'
import type { StructuralEditing } from './QuotationEditor'
import type {
  CategoryMargins,
  Currency,
  Item,
  Money as MoneyType,
  PricingRulesSettings,
  QuotationLine,
  RuleViolation,
} from '../../lib/types'

interface SharedProps {
  itemsByCode: Map<string, Item>
  violationsByLine: Map<string, RuleViolation[]>
  rules: PricingRulesSettings | undefined
  displayCurrency: Currency
  fx: WireFx
  margins: CategoryMargins | undefined
  busyLineId: string | undefined
  onChangeSellUnit: (lineId: string, sell: MoneyType) => void
  /** The printed text of a line — description and offer remark (Akhil, item 8). */
  onChangeText: (lineId: string, patch: { description?: string; offer_remarks?: string }) => void
  /**
   * Lines whose price is locked against a bulk markup change — see `quotationCommission.ts`.
   * Session-only: `Quotation Line` has no lock field yet (`D24`).
   */
  lockedLineIds: ReadonlySet<string>
  onToggleLock: (lineId: string) => void
  /** Absent on `/quote` — see `QuotationEditor`'s own header for why `/revisions` passes it. */
  structural?: StructuralEditing
}

const GROUPS = [
  { tag: 'provision' as const, icon: '🔁', label: 'Provision — from contract' },
  { tag: 'rfq' as const, icon: '⚖️', label: 'RFQ — sourced' },
]

/**
 * A group's subtotal, converted per line before summing — assuming every line shares the
 * first one's currency would mislabel the total the moment one didn't.
 *
 * Sums `l.qty` as given, so a caller passes lines whose `qty` already reflects any queued edit —
 * `lib/pricing.ts`'s `effectiveLines`, the SAME derivation `QuotationEditor`'s totals preview uses
 * (fix round, final review: I6 — see that function's own header for the bug this fixed).
 */
function subtotalOf(
  lines: QuotationLine[],
  pick: (line: QuotationLine) => MoneyType,
  currency: Currency,
  fx: WireFx,
): MoneyType {
  return {
    amount: lines.reduce((sum, l) => sum + convert(pick(l), currency, fx).amount * l.qty, 0),
    currency,
  }
}

export function QuoteLinesTable({
  lines,
  grouped,
  ...shared
}: SharedProps & { lines: QuotationLine[]; grouped: boolean }) {
  // Item, Qty, UOM, Cost/u, 🔒, Sell/u, Line — plus the remove/undo control, only on `/revisions`.
  const columnCount = shared.structural ? 8 : 7

  // Same derivation `QuotationEditor`'s totals preview uses — raw `lines` stays what gets
  // RENDERED (see `subtotalOf`'s own comment); this is only what gets SUMMED.
  const totalsLines = effectiveLines(lines, shared.structural?.pending)

  const renderRow = (line: QuotationLine) => {
    // 'add' is never THIS row's state — see `RowStructuralEditing`'s own comment in
    // `QuoteRow.tsx`. An existing line can only be resized or removed, never (re-)added.
    const rawPending = shared.structural?.pending.get(line.item_code)
    // Rebuilt rather than narrowed in place: `rawPending`'s type is one object shape with a
    // union-typed `op`, not a discriminated union of shapes, so `rawPending.op !== 'add'` alone
    // narrows the PROPERTY access, not the variable — TS still sees `rawPending` itself as
    // possibly `'add'` further down. Re-reading `.op`/`.qty` here, where the guard is in scope,
    // narrows for real.
    const rowPending = rawPending && rawPending.op !== 'add' ? { op: rawPending.op, qty: rawPending.qty } : undefined

    return (
      <QuoteRow
        key={line.line_id}
        line={line}
        item={shared.itemsByCode.get(line.item_code)}
        violations={shared.violationsByLine.get(line.line_id) ?? []}
        rules={shared.rules}
        displayCurrency={shared.displayCurrency}
        fx={shared.fx}
        // `category` is optional (Quoflow has no category concept — src/lib/types.ts); with none
        // there is no category margin to look up.
        categoryMarginPct={line.category !== undefined ? shared.margins?.[line.category] : undefined}
        locked={shared.lockedLineIds.has(line.line_id)}
        onToggleLock={() => shared.onToggleLock(line.line_id)}
        busy={shared.busyLineId === line.line_id}
        onChangeSellUnit={(sell) => shared.onChangeSellUnit(line.line_id, sell)}
        onChangeText={(patch) => shared.onChangeText(line.line_id, patch)}
        structural={
          shared.structural
            ? {
                pending: rowPending,
                onQueueQty: (qty) => shared.structural?.onQueueQty(line.item_code, qty),
                onQueueRemove: () => shared.structural?.onQueueRemove(line.item_code),
                onDiscard: () => shared.structural?.onDiscard(line.item_code),
              }
            : undefined
        }
      />
    )
  }

  // What is SHOWN: a search, then a page of 50 (Akhil, 2026-10-28 — 300-line quotations). The
  // subtotals above and the editor's totals still sum every line; only the rows are narrowed.
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const view = quoteLineView(lines, { query, page, grouped })
  const searching = query.trim() !== ''
  const paged = view.pageCount > 1

  // A group's subtotal prints once, under the LAST of its matching lines — on whichever page
  // that falls. A page that ends mid-group repeats the heading on the next one.
  const matching = grouped ? matchingLines(lines, query, true) : []
  const lastOfGroup = new Set(
    GROUPS.map(({ tag }) => matching.filter((l) => l.tag === tag).at(-1)?.line_id).filter((id): id is string => !!id),
  )

  const pager = paged ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <Button size="sm" disabled={view.page <= 1} onClick={() => setPage(view.page - 1)} aria-label="Previous page">
        ‹
      </Button>
      {Array.from({ length: view.pageCount }, (_, i) => i + 1).map((n) => (
        <Button
          key={n}
          size="sm"
          variant={n === view.page ? 'primary' : 'default'}
          onClick={() => setPage(n)}
          aria-current={n === view.page ? 'page' : undefined}
        >
          {n}
        </Button>
      ))}
      <Button size="sm" disabled={view.page >= view.pageCount} onClick={() => setPage(view.page + 1)} aria-label="Next page">
        ›
      </Button>
    </div>
  ) : null

  const summary =
    view.total === 0 ? (
      searching ? (
        <span className="pill" style={{ background: 'var(--bg)', color: 'var(--muted)' }}>
          No matches
        </span>
      ) : null
    ) : paged || searching ? (
      <span style={{ fontSize: 12, color: 'var(--muted)' }}>
        {searching ? (
          <span className="pill" style={{ marginRight: 6 }}>
            {view.total} match{view.total === 1 ? '' : 'es'}
          </span>
        ) : null}
        Lines {view.from}–{view.to} of {searching ? `${view.total} · ${lines.length} in all` : view.total}
      </span>
    ) : null

  const showToolbar = lines.length > QUOTE_PAGE_SIZE || searching

  return (
    <Card padded={false}>
      {lines.length > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 12px', borderBottom: '1px solid var(--line)' }}>
          <div className="qsearch">
            <svg className="qsearch-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="8.5" cy="8.5" r="5.5" />
              <path d="m13 13 4 4" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setPage(1)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setQuery('')
                  setPage(1)
                }
              }}
              placeholder="Search by item name, code or line # (e.g. #87)"
              aria-label="Search quotation lines"
            />
            {searching ? (
              <button
                type="button"
                className="qsearch-clear"
                aria-label="Clear search"
                title="Clear search (Esc)"
                onClick={() => {
                  setQuery('')
                  setPage(1)
                }}
              >
                ✕
              </button>
            ) : null}
          </div>
          {showToolbar ? summary : null}
          <span style={{ marginLeft: 'auto' }}>{pager}</span>
        </div>
      ) : null}
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th className="right">Qty{shared.structural ? ' ✎' : ''}</th>
            <th>UOM</th>
            <th className="right">Cost/u</th>
            {/* Narrow and first-of-the-money-columns: the lock guards the price beside it, so it
                belongs next to it rather than at the far end of the row. */}
            <th className="right" style={{ width: 44 }} title="Lock this price against bulk markup changes">
              🔒
            </th>
            <th className="right">Sell/u ✎</th>
            <th className="right">Line</th>
            {shared.structural ? <th className="right">&nbsp;</th> : null}
          </tr>
        </thead>
        <tbody>
          {lines.length === 0 ? (
            <tr>
              <td colSpan={columnCount} style={{ color: 'var(--muted)', textAlign: 'center', padding: 22 }}>
                No lines on this quotation.
              </td>
            </tr>
          ) : null}

          {grouped
            ? GROUPS.map(({ tag, icon, label }) => {
                const group = lines.filter((l) => l.tag === tag)
                const onPage = view.lines.filter((l) => l.tag === tag)
                if (onPage.length === 0) return null
                const endsHere = lastOfGroup.has(onPage[onPage.length - 1].line_id)

                // The subtotal sums the EFFECTIVE group — same qty/removal treatment as
                // `QuotationEditor`'s `previewTotals` call — not `group` itself, which still
                // carries a queued removal's full qty and the pre-edit quantity for a resize.
                const groupForTotals = totalsLines.filter((l) => l.tag === tag)
                const cost = subtotalOf(groupForTotals, (l) => l.cost_unit, shared.displayCurrency, shared.fx)
                const sell = subtotalOf(groupForTotals, (l) => l.sell_unit, shared.displayCurrency, shared.fx)

                return (
                  // A keyed Fragment, not a wrapper element — a group is a header row, its
                  // lines and a subtotal row, all siblings inside <tbody>.
                  <Fragment key={tag}>
                    <tr style={{ background: 'var(--bg)' }}>
                      <td colSpan={columnCount} style={{ fontWeight: 800, fontSize: 12 }}>
                        {icon} {label} · {group.length} line(s)
                      </td>
                    </tr>
                    {onPage.map(renderRow)}
                    {endsHere ? (
                    <tr>
                      <td colSpan={columnCount - (shared.structural ? 2 : 1)} className="right" style={{ color: 'var(--muted)', fontSize: 12 }}>
                        subtotal · cost <Money value={cost} /> · sell
                      </td>
                      <td className="right mono" style={{ fontWeight: 800 }}>
                        <Money value={sell} />
                      </td>
                      {shared.structural ? <td /> : null}
                    </tr>
                    ) : null}
                  </Fragment>
                )
              })
            : /*
                THE CUSTOMER'S OWN ORDER — by serial number (`inSerialOrder`), so 1, 2, 3 read
                down the page even when the server holds a revision-added line LAST: `apply_
                revisions` appends, and a line numbered 2 to replace a removed line 2 must sit at 2
                (Akhil, 2026-09-15). Unnumbered lines follow, in the order they came.

                This used to sort provision lines to the top ("the prototype's order"). It was
                INERT until 2026-09-05 — `toQuotationLine` hardcoded every line's tag to `'rfq'`,
                so the comparator never returned anything but 0. The moment `tagFromSource` began
                reading the real route, the sort woke up and scrambled the numbering: #6 and an
                unnumbered line jumped above #1.

                A customer reading their own list against ours has to find item 3 at 3 — the same
                rule `quotationFromAllocation.ts` keeps `enquiry_row_no` contiguous for. Grouping
                by route is still available, deliberately, behind the "Group items" checkbox.
              */
              view.lines.map(renderRow)}
          {lines.length > 0 && view.total === 0 ? (
            <tr>
              <td colSpan={columnCount} style={{ color: 'var(--muted)', textAlign: 'center', padding: 22 }}>
                No lines match “{query.trim()}”.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {pager ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, padding: '10px 12px', borderTop: '1px solid var(--line)' }}>
          {summary}
          {pager}
        </div>
      ) : null}
    </Card>
  )
}
